import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormEvent, useState } from 'react'

import { returnCasesHttp } from '@/services/returnCases'
import type {
  SalaryCommandBase,
  SalaryComponentInput,
  SalaryEmployerInput,
  SalaryEmploymentInput,
  SalaryHraInput,
  SalaryLtaInput,
  SalaryModuleReadModel,
  SalaryReliefInput,
  SalaryResourceResponse,
  SalarySourceType,
} from '@/types/returnCase.types'

export interface SalaryPanelClient {
  salaryModule: (caseId: string) => Promise<SalaryModuleReadModel>
  createSalaryEmployer: (
    caseId: string,
    input: SalaryEmployerInput
  ) => Promise<SalaryResourceResponse>
  createSalaryEmployment: (
    caseId: string,
    input: SalaryEmploymentInput
  ) => Promise<SalaryResourceResponse>
  createSalaryComponent: (
    caseId: string,
    input: SalaryComponentInput
  ) => Promise<SalaryResourceResponse>
  createSalaryHraPeriod: (caseId: string, input: SalaryHraInput) => Promise<SalaryResourceResponse>
  createSalaryLtaJourney: (caseId: string, input: SalaryLtaInput) => Promise<SalaryResourceResponse>
  createSalaryReliefItem: (
    caseId: string,
    input: SalaryReliefInput
  ) => Promise<SalaryResourceResponse>
}
interface Props {
  customerName?: string
  caseId: string
  canEdit: boolean
  onChanged: () => Promise<void>
  client?: SalaryPanelClient
  sourceType?: SalarySourceType
}
type EntryKind = 'employer' | 'employment' | 'component' | 'hra' | 'lta' | 'relief'

const value = (data: FormData, key: string) => String(data.get(key) ?? '').trim()
const checked = (data: FormData, key: string) => data.get(key) === 'on'

export default function SalaryModulePanel({
  caseId,
  customerName,
  canEdit,
  onChanged,
  client = returnCasesHttp,
  sourceType = 'STAFF_MANUAL',
}: Props) {
  const queryClient = useQueryClient()
  const [entryKind, setEntryKind] = useState<EntryKind>('employer')
  const [error, setError] = useState('')
  const query = useQuery({
    queryKey: ['salary-module', caseId],
    queryFn: () => client.salaryModule(caseId),
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['salary-module', caseId] })
    await onChanged()
  }
  const create = useMutation({
    mutationFn: async (form: HTMLFormElement) => {
      if (!query.data) throw new Error('Salary module has not loaded')
      const data = new FormData(form)
      const base: SalaryCommandBase = {
        expected_revision: query.data.case_revision,
        source_type: sourceType,
        source_reference: value(data, 'source_reference') || 'web:salary-workspace',
        source_metadata: { interface: 'salary-module-v1' },
        reason: value(data, 'reason'),
      }
      if (entryKind === 'employer')
        return client.createSalaryEmployer(caseId, {
          ...base,
          name: value(data, 'name'),
          tan: value(data, 'tan') || undefined,
          pan: value(data, 'pan') || undefined,
          address: { city: value(data, 'city') },
          employer_type: 'PRIVATE',
        })
      if (entryKind === 'employment')
        return client.createSalaryEmployment(caseId, {
          ...base,
          employer_id: value(data, 'employer_id'),
          employment_kind: value(data, 'employment_kind') as 'EMPLOYMENT' | 'RETIREMENT_PENSION',
          start_date: value(data, 'start_date'),
          end_date: value(data, 'end_date'),
          payroll_regime: value(data, 'regime') as 'OLD' | 'NEW' | 'UNKNOWN',
          prior_employer_income_declared: checked(data, 'prior_employer_income_declared'),
        })
      if (entryKind === 'component') {
        const kind = value(data, 'component_kind')
        return client.createSalaryComponent(caseId, {
          ...base,
          employment_id: value(data, 'employment_id'),
          component_kind: kind as 'BASIC',
          section_group: kind === 'FAMILY_PENSION' ? 'OTHER_SOURCES_REDIRECT' : 'SECTION_17_1',
          amount: value(data, 'amount'),
          description: value(data, 'description') || undefined,
          source_period_start: value(data, 'start_date'),
          source_period_end: value(data, 'end_date'),
        })
      }
      if (entryKind === 'hra')
        return client.createSalaryHraPeriod(caseId, {
          ...base,
          employment_id: value(data, 'employment_id'),
          start_date: value(data, 'start_date'),
          end_date: value(data, 'end_date'),
          city: value(data, 'city'),
          city_class: value(data, 'city_class') as 'METRO' | 'NON_METRO',
          actual_hra: value(data, 'actual_hra'),
          rent_paid: value(data, 'rent_paid'),
          basic_salary: value(data, 'basic_salary'),
          da_retirement: value(data, 'da_retirement') || '0',
          turnover_commission: value(data, 'turnover_commission') || '0',
          landlord_name: value(data, 'landlord_name') || undefined,
          landlord_pan: value(data, 'landlord_pan') || undefined,
          residence_owned_by_taxpayer: checked(data, 'residence_owned_by_taxpayer'),
          rent_evidence_status: 'REQUIRED',
          regime: value(data, 'regime') as 'OLD' | 'NEW',
        })
      if (entryKind === 'lta')
        return client.createSalaryLtaJourney(caseId, {
          ...base,
          employment_id: value(data, 'employment_id'),
          departure_date: value(data, 'start_date'),
          return_date: value(data, 'end_date'),
          origin: value(data, 'origin'),
          destination: value(data, 'destination'),
          domestic: checked(data, 'domestic'),
          travel_mode: value(data, 'travel_mode') as 'AIR' | 'RAIL' | 'PUBLIC_TRANSPORT' | 'OTHER',
          travel_class: value(data, 'travel_class'),
          shortest_route_confirmed: checked(data, 'shortest_route_confirmed'),
          actual_fare: value(data, 'actual_fare'),
          eligible_fare_cap: value(data, 'eligible_fare_cap'),
          reimbursement_received: value(data, 'reimbursement_received'),
          block_start_year: Number(value(data, 'block_start_year')),
          block_end_year: Number(value(data, 'block_start_year')) + 3,
          prior_claim_count: Number(value(data, 'prior_claim_count') || 0),
          carryover_claim: checked(data, 'carryover_claim'),
          evidence_status: 'REQUIRED',
          regime: value(data, 'regime') as 'OLD' | 'NEW',
          travellers: [
            { name: value(data, 'traveller_name'), relationship: 'SELF', dependent: false },
          ],
        })
      return client.createSalaryReliefItem(caseId, {
        ...base,
        income_kind: value(data, 'income_kind') as 'SALARY_ARREARS',
        amount: value(data, 'amount'),
        received_on: value(data, 'received_on'),
        year_allocations: [
          { financial_year: value(data, 'financial_year'), amount: value(data, 'amount') },
        ],
        evidence_status: 'REQUIRED',
        form10e_confirmation: checked(data, 'form10e_confirmation'),
      })
    },
    onSuccess: async () => {
      setError('')
      await refresh()
    },
    onError: async cause => {
      setError(cause instanceof Error ? cause.message : 'Entry was rejected')
      await refresh()
    },
  })

  if (query.isLoading)
    return <section className="mt-8 border-t border-border pt-6">Loading salary module…</section>
  if (query.isError || !query.data)
    return (
      <section className="mt-8 border-t border-border pt-6">
        <div className="alert alert-error" role="alert">
          Salary module is unavailable. No data was changed.
        </div>
      </section>
    )
  const salary = query.data
  const employmentOptions = salary.employments.map(item => ({
    id: item.id,
    label: `${item.start_date} – ${item.end_date}`,
  }))

  return (
    <section className="mt-8 border-t border-border pt-6" aria-labelledby="salary-module-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="salary-module-title" className="text-lg font-semibold">
            Salary, pension, HRA and LTA
          </h2>
          <p className="text-sm text-text-secondary">
            Revision {salary.case_revision} · governed pack {salary.rule_pack.version}
          </p>
        </div>
        <span className="badge">Form 10E {salary.form10e_readiness.status}</span>
      </div>
      {error && (
        <div className="alert alert-error mt-4" role="alert">
          {error} The latest server revision has been reloaded.
        </div>
      )}
      {salary.warnings.length > 0 && (
        <div className="alert alert-warning mt-4" role="status">
          <ul>
            {salary.warnings.map(item => (
              <li key={`${item.code}-${item.resource_ids.join('-')}`}>{item.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <article className="rounded border border-border p-4">
            <h3 className="font-medium">Employers and income</h3>
            <p className="text-sm text-text-secondary">
              {salary.employers.length} employers · {salary.employments.length} employments ·{' '}
              {salary.components.length} components
            </p>
            {salary.components.map(item => (
              <div key={item.id} className="mt-2 rounded bg-surface-secondary p-2 text-sm">
                {item.component_kind}: ₹{item.amount} <span className="badge">{item.tax_head}</span>
              </div>
            ))}
          </article>
          <article className="rounded border border-border p-4">
            <h3 className="font-medium">HRA explanations</h3>
            {salary.hra_explanations.length === 0 && (
              <p className="text-sm text-text-secondary">No residence periods entered.</p>
            )}
            {salary.hra_explanations.map(item => (
              <div key={item.period_id} className="mt-2 rounded bg-surface-secondary p-3 text-sm">
                <div className="flex justify-between">
                  <span>Exemption ₹{item.exemption}</span>
                  <span className="badge">{item.status}</span>
                </div>
                <p className="mt-1 text-text-secondary">{item.reasons.join(' ')}</p>
                <p className="mt-1 font-mono text-xs">
                  {item.rule_key} · {item.rule_version}
                </p>
              </div>
            ))}
          </article>
          <article className="rounded border border-border p-4">
            <h3 className="font-medium">LTA explanations</h3>
            {salary.lta_explanations.length === 0 && (
              <p className="text-sm text-text-secondary">No journeys entered.</p>
            )}
            {salary.lta_explanations.map(item => (
              <div key={item.journey_id} className="mt-2 rounded bg-surface-secondary p-3 text-sm">
                ₹{item.exemption} · <span className="badge">{item.status}</span>
                <p className="mt-1 text-text-secondary">{item.reasons.join(' ')}</p>
              </div>
            ))}
          </article>
          <article className="rounded border border-border p-4">
            <h3 className="font-medium">Evidence and history</h3>
            <ul className="mt-2 space-y-1 text-sm">
              {salary.evidence_requirements.map(item => (
                <li key={item.id}>
                  {item.label} <span className="badge">{item.status}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-text-secondary">
              {salary.history.length} immutable audit entries. Evidence can only be accepted after
              an immutable document version is bound.
            </p>
          </article>
        </div>

        {canEdit && (
          <form
            className="rounded border border-border p-4"
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault()
              create.mutate(event.currentTarget)
            }}
          >
            <h3 className="font-medium">Add structured salary detail</h3>
            <label className="mt-3 block text-sm" htmlFor="salary-entry-kind">
              Detail type
            </label>
            <select
              id="salary-entry-kind"
              className="input mt-1 w-full"
              value={entryKind}
              onChange={event => setEntryKind(event.target.value as EntryKind)}
            >
              <option value="employer">Employer</option>
              <option value="employment">Employment or retirement pension</option>
              <option value="component">Salary or pension component</option>
              <option value="hra">HRA residence period</option>
              <option value="lta">LTA journey</option>
              <option value="relief">Arrears / Form 10E readiness</option>
            </select>
            {entryKind === 'employer' && (
              <>
                <Field name="name" label="Employer name" />
                <Field name="tan" label="TAN (optional)" required={false} />
                <Field name="pan" label="PAN (optional)" required={false} />
                <Field name="city" label="City" />
              </>
            )}
            {(entryKind === 'component' || entryKind === 'hra' || entryKind === 'lta') && (
              <Select name="employment_id" label="Employment" options={employmentOptions} />
            )}
            {entryKind === 'employment' && (
              <>
                <Select
                  name="employer_id"
                  label="Employer"
                  options={salary.employers.map(item => ({ id: item.id, label: item.name }))}
                />
                <Select
                  name="employment_kind"
                  label="Employment kind"
                  options={[
                    { id: 'EMPLOYMENT', label: 'Employment' },
                    { id: 'RETIREMENT_PENSION', label: 'Retirement pension' },
                  ]}
                />
                <DateRange />
                <Regime />
              </>
            )}
            {entryKind === 'component' && (
              <>
                <Select
                  name="component_kind"
                  label="Component"
                  options={[
                    'BASIC',
                    'HRA',
                    'BONUS',
                    'PERQUISITE',
                    'RETIREMENT_PENSION',
                    'FAMILY_PENSION',
                    'ARREARS',
                    'ADVANCE',
                    'TDS',
                  ].map(id => ({ id, label: id.replace(/_/g, ' ') }))}
                />
                <Field name="amount" label="Amount (₹)" type="number" />
                <Field name="description" label="Description" required={false} />
                <DateRange />
              </>
            )}
            {entryKind === 'hra' && (
              <>
                <DateRange />
                <Field name="city" label="Residence city" />
                <Select
                  name="city_class"
                  label="City class"
                  options={[
                    { id: 'METRO', label: 'Metro' },
                    { id: 'NON_METRO', label: 'Non-metro' },
                  ]}
                />
                <Field name="actual_hra" label="Actual HRA (₹)" type="number" />
                <Field name="rent_paid" label="Rent paid (₹)" type="number" />
                <Field name="basic_salary" label="Basic salary for period (₹)" type="number" />
                <Field
                  name="da_retirement"
                  label="Retirement-benefit DA (₹)"
                  type="number"
                  required={false}
                />
                <Field
                  name="turnover_commission"
                  label="Turnover commission (₹)"
                  type="number"
                  required={false}
                />
                <Field name="landlord_name" label="Landlord name" required={false} />
                <Field name="landlord_pan" label="Landlord PAN" required={false} />
                <Regime />
              </>
            )}
            {entryKind === 'lta' && (
              <>
                <DateRange />
                <Field name="origin" label="Origin" />
                <Field name="destination" label="Destination" />
                <Select
                  name="travel_mode"
                  label="Travel mode"
                  options={['AIR', 'RAIL', 'PUBLIC_TRANSPORT', 'OTHER'].map(id => ({
                    id,
                    label: id.replace(/_/g, ' '),
                  }))}
                />
                <Field name="travel_class" label="Travel class" />
                <Field name="actual_fare" label="Actual fare (₹)" type="number" />
                <Field name="eligible_fare_cap" label="Eligible fare cap (₹)" type="number" />
                <Field name="reimbursement_received" label="Reimbursement (₹)" type="number" />
                <Field name="block_start_year" label="Block start year" type="number" />
                <Field name="prior_claim_count" label="Claims already used" type="number" />
                <Field
                  name="traveller_name"
                  label="Employee traveller name"
                  defaultValue={customerName}
                />
                <Check name="domestic" label="Travel is within India" />
                <Check name="shortest_route_confirmed" label="Shortest-route fare confirmed" />
                <Regime />
              </>
            )}
            {entryKind === 'relief' && (
              <>
                <Select
                  name="income_kind"
                  label="Relief income kind"
                  options={[
                    'SALARY_ARREARS',
                    'SALARY_ADVANCE',
                    'FAMILY_PENSION_ARREARS',
                    'GRATUITY',
                    'TERMINATION_COMPENSATION',
                    'COMMUTED_PENSION',
                  ].map(id => ({ id, label: id.replace(/_/g, ' ') }))}
                />
                <Field name="amount" label="Amount (₹)" type="number" />
                <Field name="received_on" label="Received on" type="date" />
                <Field name="financial_year" label="Attributed financial year (YYYY-YY)" />
                <Check name="form10e_confirmation" label="Include in Form 10E preparation" />
              </>
            )}
            <Field name="source_reference" label="Source reference" required={false} />
            <Field name="reason" label="Reason for entry" />
            <button className="btn btn-primary mt-4" type="submit" disabled={create.isPending}>
              Save at revision {salary.case_revision}
            </button>
          </form>
        )}
      </div>
    </section>
  )
}

function Field({
  name,
  label,
  type = 'text',
  required = true,
  defaultValue,
}: {
  name: string
  label: string
  type?: string
  required?: boolean
  defaultValue?: string
}) {
  return (
    <label className="mt-3 block text-sm">
      {label}
      <input
        className="input mt-1 w-full"
        name={name}
        defaultValue={defaultValue}
        type={type}
        required={required}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? '0.01' : undefined}
      />
    </label>
  )
}
function Select({
  name,
  label,
  options,
}: {
  name: string
  label: string
  options: Array<{ id: string; label: string }>
}) {
  return (
    <label className="mt-3 block text-sm">
      {label}
      <select className="input mt-1 w-full" name={name} required>
        <option value="">Select…</option>
        {options.map(item => (
          <option key={item.id} value={item.id}>
            {item.label}
          </option>
        ))}
      </select>
    </label>
  )
}
function Check({ name, label }: { name: string; label: string }) {
  return (
    <label className="mt-3 flex items-center gap-2 text-sm">
      <input name={name} type="checkbox" />
      {label}
    </label>
  )
}
function DateRange() {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field name="start_date" label="Start / departure" type="date" />
      <Field name="end_date" label="End / return" type="date" />
    </div>
  )
}
function Regime() {
  return (
    <Select
      name="regime"
      label="Regime input"
      options={[
        { id: 'OLD', label: 'Old' },
        { id: 'NEW', label: 'New' },
      ]}
    />
  )
}
